import {
  DocumentNode,
  OperationVariables,
  QueryHookOptions,
  QueryResult,
  useQuery as _useQuery
} from "@apollo/client"
import { graphql } from "@apollo/client/react/hoc"
import camelCase from "lodash/camelCase"
import get from "lodash/get"
import has from "lodash/has"
import React, { Component, ComponentType, ReactNode, useState } from "react"

// Retains expected behavior of Apollo Client v2 where data is NOT reset to undefined when loading state changes.
export const useQuery = <
  TData = any,
  TVariables extends OperationVariables = OperationVariables
>(
  q: DocumentNode,
  options: QueryHookOptions<TData, TVariables> = {}
): QueryResult<TData, TVariables> => {
  const query = _useQuery<TData, TVariables>(q, options)
  query.data = query.data ?? query.previousData
  return query
}

// Recursively flatten all { edges: { node: T }[] } to T[]
// Handles optional, nullable, and union edge/node types
export type FlattenEdges<T> = T extends {
  edges?: (infer E)[] | null | undefined
}
  ? E extends {
      node?: infer N | null | undefined
    }
    ? Array<FlattenEdges<N>>
    : []
  : T extends object
  ? { [K in keyof T]: FlattenEdges<T[K]> }
  : T

// flattenEdges({ foo: { edges: [{ node: { bar: 1 } }] } })
// => { foo: [{ bar: 1 }] }
export function flattenEdges<T>(obj: T): FlattenEdges<T> {
  const isObject = Object.prototype.toString.call(obj) === "[object Object]"
  if (!isObject) return obj as FlattenEdges<T>
  let result: any = {}
  for (let key in obj) {
    if (key === "edges") {
      result = ((obj[key] || []) as any[]).map((edge) =>
        flattenEdges(edge.node)
      )
      // this is probably a bad idea
      // TODO: remove these, or type them properly. Currently choosing to not type them
      // so that we can update consumers incrementally, removing the dependency on these fields.
    } else if (key === "pageInfo") {
      result.pageInfo = obj[key]
    } else if (key === "total") {
      result.total = obj[key]
    } else {
      result[key] = flattenEdges(obj[key])
    }
  }
  return result
}

// util for throwing an error if the expected pageInfo path does not exist in the query data
const _errorOnNonExistyPageInfoPath = (data: any, path: string): void => {
  if (has(data, path) && !has(data, `${path}.pageInfo`)) {
    throw new Error(
      `Attempted to setup paginator for "${path}" but unable as pageInfo does not exist in the query data. Be sure to add pageInfo to your query.`
    )
  }
}

// resolves data path (related to `useQuery` and `graphql` data shape differences)
const _getDataPath = (data: any, path: string): string => {
  if (data && data.data) return `data.${path}`
  return path
}

// loadMoreAuthors: createPaginator(data, "resource.authors", "authorCursor")
// => () => promise

// NOTE: in order for the loading prop to be updated when calling `fetchMore`
// you must pass notifyOnNetworkStatusChange: true, in the GQL query options
export function createPaginator(
  data: any,
  path: string,
  cursorVar: string = "cursor"
): () => Promise<any> {
  const dataPath = _getDataPath(data, path)
  // ensure we have pageInfo
  _errorOnNonExistyPageInfoPath(data, dataPath)

  const endCursorPath = `${dataPath}.pageInfo.endCursor`

  return function () {
    return data.fetchMore({
      variables: {
        ...data.variables,
        [cursorVar]: get(data, endCursorPath)
      }
    })
  }
}

interface UsePaginatorOptions {
  data: any
  path: string
  cursorKey?: string // or something like "cohorts_cursor"
}

export interface UsePaginatorReturn {
  hasMore: boolean
  loadingMore: boolean
  loadMore: () => Promise<void>
}

export const usePaginator = ({
  data,
  path,
  cursorKey = "cursor" // or something like "cohorts_cursor"
}: UsePaginatorOptions): UsePaginatorReturn => {
  const [loading, setLoading] = useState(false)

  const dataPath = _getDataPath(data, path)
  // ensure we have pageInfo
  _errorOnNonExistyPageInfoPath(data, dataPath)

  const hasMore = get(data, `${dataPath}.pageInfo.hasNextPage`)

  const paginator = createPaginator(data, path, cursorKey)

  const loadMore = async (): Promise<void> => {
    if (hasMore) {
      setLoading(true)
      await paginator()
      setLoading(false)
    }
  }

  return {
    hasMore,
    loadingMore: loading,
    loadMore
  }
}

interface WithPaginatorOptions {
  path: string
  cursorKey?: string
  loadingKey?: string
}

// Handles `hasMore`, `loadMore`, and `loadingMore` key
// NOTE: depends on parent Component to pass `data` prop down
export const withPaginator =
  ({ path, cursorKey, loadingKey = "" }: WithPaginatorOptions) =>
  <P extends { data: any }>(ComposedComponent: ComponentType<P>) =>
  (props: P): ReactNode => {
    const { loadMore, hasMore, loadingMore } = usePaginator({
      data: props.data,
      path,
      cursorKey
    })

    const hasMorePropName = camelCase(`hasMore ${loadingKey}`)
    const loadMorePropName = camelCase(`loadMore ${loadingKey}`)
    const loadingMorePropName = camelCase(`loadingMore ${loadingKey}`)

    const passProps = {
      [hasMorePropName]: hasMore,
      [loadingMorePropName]: loadingMore,
      [loadMorePropName]: loadMore
    }

    return React.createElement(ComposedComponent, { ...props, ...passProps })
  }

interface MutationConfig {
  name: string
  key: string
}

interface WithMutationPropsState {
  saving: boolean
  saved: boolean
  error: any
  result: any
}

export const withMutationProps =
  (mutationConfig: MutationConfig) =>
  (mutation: any, options: any) =>
  (C: ComponentType<any>) => {
    let mutateFunc: any = null

    class WithMutationProps extends Component<any, WithMutationPropsState> {
      static displayName = `WithMutationPropsFor_${mutationConfig.key}`

      state: WithMutationPropsState = {
        saving: false,
        saved: false,
        error: null,
        result: null
      }

      ComposedComponent = graphql(mutation, {
        ...options,
        props: (props: any) => {
          mutateFunc = props.mutate
          const passProps = options.props
            ? options.props({
                ...props,
                mutate: this.mutate
              })
            : {}
          return {
            ...passProps
          }
        }
      })(C)

      mutate = async (...args: any[]): Promise<any> => {
        this.setState({
          saving: true,
          saved: false,
          error: null
        })
        try {
          const result = await mutateFunc(...args)
          this.setState({
            result: get(result, `data.${mutationConfig.name}`) || result,
            saved: true
          })
          return result
        } catch (error) {
          this.setState({
            error: error
          })
        } finally {
          this.setState({
            saving: false
          })
        }
      }

      render(): ReactNode {
        return React.createElement(this.ComposedComponent, {
          ...this.props,
          [`${mutationConfig.key}_saving`]: this.state.saving,
          [`${mutationConfig.key}_saved`]: this.state.saved,
          [`${mutationConfig.key}_error`]: this.state.error,
          [`${mutationConfig.key}_result`]: this.state.result
        })
      }
    }

    return WithMutationProps
  }
